// ============================================================
// VidyaTrack — Login Page  (production)
// ============================================================

import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { FiMail, FiLock, FiEye, FiEyeOff } from "react-icons/fi";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "react-toastify";
import useSEO from "../../seo/useSEO";

export default function LoginPage() {
  useSEO({ title: "Sign In", path: window.location.pathname, noindex: true });
  const [email,   setEmail]   = useState("");
  const [pw,      setPw]      = useState("");
  const [showPw,  setShowPw]  = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors,  setErrors]  = useState({});

  const { login } = useAuth();
  const navigate  = useNavigate();

  const validate = () => {
    const e = {};
    if (!email.trim())              e.email = "Email address is required";
    else if (!/\S+@\S+\.\S+/.test(email)) e.email = "Enter a valid email address";
    if (!pw)                        e.pw    = "Password is required";
    else if (pw.length < 6)         e.pw    = "Password must be at least 6 characters";
    return e;
  };

  const handleSubmit = async (evt) => {
    evt.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({}); setLoading(true);
    try {
      const res = await login(email.trim(), pw);
      if (res.success) {
        toast.success("Welcome back!");
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(err.message);
      setErrors({ form: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
    }}>
      {/* ── Left: Brand panel ── */}
      <div style={{
        background: "linear-gradient(150deg,#054bb4 0%,#1a3a8a 100%)",
        padding: "48px",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
      }}>
        {/* Decorative circles */}
        <div style={{ position:"absolute", width:400, height:400, borderRadius:"50%", background:"rgba(255,255,255,.04)", top:-120, left:-120 }}/>
        <div style={{ position:"absolute", width:300, height:300, borderRadius:"50%", background:"rgba(255,255,255,.04)", bottom:-80, right:-80 }}/>

        <Link to="/" style={{ position:"relative", display:"inline-flex", alignItems:"center", gap:10, textDecoration:"none", marginBottom:"auto" }}>
          <img src="/vtDark.png" alt="VidyaTrack" style={{ width:42, height:42, objectFit:"contain" }} />
          <div>
            <div style={{ fontFamily:"'Poppins',sans-serif", fontWeight:800, fontSize:"1rem", color:"#fff", lineHeight:1 }}>VidyaTrack</div>
            <div style={{ fontSize:".62rem", color:"rgba(255,255,255,.5)", marginTop:2 }}>School Management System</div>
          </div>
        </Link>

        <div style={{ position:"relative", flex:1, display:"flex", flexDirection:"column", justifyContent:"center" }}>
          <div style={{ fontSize:"2.8rem", marginBottom:18 }}>🏫</div>
          <h2 style={{ fontFamily:"'Poppins',sans-serif", fontWeight:900, fontSize:"1.9rem", color:"#fff", lineHeight:1.2, marginBottom:14 }}>
            Manage Your<br/>School Digitally
          </h2>
          <p style={{ color:"rgba(255,255,255,.65)", fontSize:".92rem", lineHeight:1.8, marginBottom:32, maxWidth:300 }}>
            A unified platform connecting teachers, students and parents — built for schools in Nepal.
          </p>
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {[
              { icon:"📚", text:"Assign & track homework with deadlines" },
              { icon:"✅", text:"Mark and monitor daily attendance" },
              { icon:"📢", text:"Share notices and announcements instantly" },
              { icon:"📊", text:"Analytics and reports for every student" },
            ].map(f => (
              <div key={f.text} style={{ display:"flex", alignItems:"center", gap:12 }}>
                <div style={{ width:34, height:34, background:"rgba(255,255,255,.1)", borderRadius:9, display:"flex", alignItems:"center", justifyContent:"center", fontSize:".95rem", flexShrink:0 }}>{f.icon}</div>
                <span style={{ color:"rgba(255,255,255,.8)", fontSize:".84rem" }}>{f.text}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ position:"relative", fontSize:".72rem", color:"rgba(255,255,255,.28)", marginTop:36 }}>
          🔒 Secured by Firebase Authentication &nbsp;·&nbsp; Made in Nepal 🇳🇵
        </div>
      </div>

      {/* ── Right: Form ── */}
      <div style={{ background:"#f4f7fd", display:"flex", alignItems:"center", justifyContent:"center", padding:"32px 24px", overflowY:"auto" }}>
        <div style={{ width:"100%", maxWidth:420 }}>

          <div style={{ textAlign:"center", marginBottom:32 }}>
            <h1 style={{ fontFamily:"'Poppins',sans-serif", fontWeight:800, fontSize:"1.75rem", color:"var(--gray-900)", marginBottom:6 }}>
              Sign In
            </h1>
            <p style={{ color:"var(--gray-500)", fontSize:".88rem" }}>
              Enter your credentials to access your dashboard
            </p>
          </div>

          {errors.form && (
            <div style={{ background:"#fef2f2", border:"1px solid #fca5a5", borderRadius:"var(--r8)", padding:"11px 14px", marginBottom:16, fontSize:".84rem", color:"var(--red-600)", display:"flex", gap:8, alignItems:"center" }}>
              <span>⚠️</span> {errors.form}
            </div>
          )}

          <div style={{ background:"#fff", borderRadius:"var(--r16)", boxShadow:"0 4px 14px rgba(0,0,0,.08)", padding:"30px", border:"1px solid var(--border)" }}>
            <form onSubmit={handleSubmit} noValidate>

              {/* Email */}
              <div className="form-group">
                <label className="form-label">Email Address</label>
                <div className="input-wrap">
                  <FiMail className="i-left" size={15}/>
                  <input
                    type="email"
                    className={`form-control pl${errors.email ? " error" : ""}`}
                    placeholder="your@school.com"
                    value={email}
                    autoComplete="email"
                    onChange={e => { setEmail(e.target.value); setErrors(p => ({...p, email:"", form:""})); }}
                  />
                </div>
                {errors.email && <div className="form-error">⚠ {errors.email}</div>}
              </div>

              {/* Password */}
              <div className="form-group">
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:5 }}>
                  <label className="form-label" style={{ margin:0 }}>Password</label>
                  <Link to="/forgot-password" style={{ fontSize:".78rem", color:"var(--blue-700)", fontWeight:600 }}>
                    Forgot password?
                  </Link>
                </div>
                <div className="input-wrap">
                  <FiLock className="i-left" size={15}/>
                  <input
                    type={showPw ? "text" : "password"}
                    className={`form-control pl pr${errors.pw ? " error" : ""}`}
                    placeholder="Your password"
                    value={pw}
                    autoComplete="current-password"
                    onChange={e => { setPw(e.target.value); setErrors(p => ({...p, pw:"", form:""})); }}
                  />
                  <button
                    type="button"
                    className="i-right"
                    onClick={() => setShowPw(v => !v)}
                    style={{ background:"none", border:"none", cursor:"pointer", display:"flex", alignItems:"center", color:"var(--gray-400)" }}
                    aria-label={showPw ? "Hide password" : "Show password"}
                  >
                    {showPw ? <FiEyeOff size={15}/> : <FiEye size={15}/>}
                  </button>
                </div>
                {errors.pw && <div className="form-error">⚠ {errors.pw}</div>}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary btn-full"
                style={{ padding:"12px", fontSize:".95rem", marginTop:4 }}
              >
                {loading
                  ? <><div className="spinner spinner-sm"/> Signing in…</>
                  : "Sign In →"
                }
              </button>
            </form>
          </div>

          <p style={{ textAlign:"center", marginTop:20, fontSize:".82rem", color:"var(--gray-400)" }}>
            <Link to="/" style={{ color:"var(--blue-700)", fontWeight:600 }}>← Back to Homepage</Link>
          </p>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          div[style*="grid-template-columns: 1fr 1fr"] { grid-template-columns: 1fr !important; }
          div[style*="background: linear-gradient(150deg,#054bb4"] { display: none !important; }
        }
      `}</style>
    </div>
  );
}
